/**
 * Master Kaizen Sensei Message Library
 * Provides context-aware messages for the Mind Warriors Dojo theme
 */

export type MessageContext =
  | 'lesson_complete'
  | 'wrong_answer'
  | 'hint'
  | 'assessment_start'
  | 'assessment_complete'
  | 'practice_greeting'
  | 'timeout'
  | 'mastery';

export type MessageType = 'encouragement' | 'wisdom' | 'celebration' | 'guidance';

/**
 * Lesson completion messages
 */
export const LESSON_COMPLETE_MESSAGES = [
  "Well done, young warrior! You have mastered this ancient form.",
  "Excellent! Mathematical Order grows stronger with your progress.",
  "Your discipline shines through. This form is now yours to command.",
  "A true warrior's precision! This technique bends to your will.",
  "The path of mastery reveals itself through your dedication.",
];

/**
 * Wrong answer encouragement messages
 */
export const WRONG_ANSWER_MESSAGES = [
  "Even masters stumble. The path to mastery requires patience, young warrior.",
  "This challenge tests your resolve. Each attempt teaches wisdom.",
  "In every mistake lies the seed of understanding. Rise and continue.",
  "The strongest warriors are forged through persistence. Try again.",
  "Difficulty is but a teacher in disguise. Reflect and advance.",
];

/**
 * Hint/wisdom delivery messages
 */
export const HINT_MESSAGES = [
  "Let me illuminate the path ahead, young warrior...",
  "Ancient wisdom suggests this approach...",
  "Consider this guidance on your journey...",
  "The masters teach us to observe carefully...",
  "A moment of reflection reveals the way...",
];

/**
 * Assessment start messages
 */
export const ASSESSMENT_START_MESSAGES = [
  "The trial begins, warrior. Show what you have learned.",
  "Enter the proving grounds with confidence. Your training has prepared you.",
  "Let your skills flow naturally in this trial.",
  "The dojo watches. Demonstrate your mastery.",
];

/**
 * Practice greeting messages
 */
export const PRACTICE_GREETING_MESSAGES = [
  "Welcome back to the dojo, warrior. Your dedication brings honor.",
  "The path of continuous improvement begins with this step.",
  "Another day, another opportunity for growth. Train well.",
  "Your return to training shows true warrior spirit.",
];

/**
 * Timeout messages
 */
export const TIMEOUT_MESSAGES = [
  "The gate closes, but wisdom is found in reflection, not haste.",
  "Time has passed, yet your journey continues. What have you learned?",
  "Even in time's constraint, you have shown your resolve.",
];

/**
 * Mastery celebration messages
 */
export const MASTERY_MESSAGES = [
  "Extraordinary! You have transcended mere practice to achieve mastery!",
  "The ancient techniques bow before your skill, young master!",
  "Witness the birth of a true mathematical warrior! This mastery is legendary!",
];

/**
 * Get a random message from a message array
 */
function getRandomMessage(messages: string[]): string {
  return messages[Math.floor(Math.random() * messages.length)];
}

/**
 * Get a context-appropriate message from Master Kaizen
 */
export function getSenseiMessage(context: MessageContext): string {
  switch (context) {
    case 'lesson_complete':
      return getRandomMessage(LESSON_COMPLETE_MESSAGES);
    case 'wrong_answer':
      return getRandomMessage(WRONG_ANSWER_MESSAGES);
    case 'hint':
      return getRandomMessage(HINT_MESSAGES);
    case 'assessment_start':
      return getRandomMessage(ASSESSMENT_START_MESSAGES);
    case 'assessment_complete':
      return "Your journey through the trial reveals much, young warrior.";
    case 'practice_greeting':
      return getRandomMessage(PRACTICE_GREETING_MESSAGES);
    case 'timeout':
      return getRandomMessage(TIMEOUT_MESSAGES);
    case 'mastery':
      return getRandomMessage(MASTERY_MESSAGES);
    default:
      return "Continue on your path, warrior.";
  }
}

/**
 * Generate dynamic assessment results recommendation message
 */
export function getAssessmentRecommendation(params: {
  strongSkills: string[];
  weakSkills: string[];
  overallScore: number;
}): string {
  const { strongSkills, weakSkills, overallScore } = params;

  if (overallScore >= 80) {
    const skillsText = strongSkills.length > 0
      ? `Your mastery of ${strongSkills.join(', ')} is impressive.`
      : 'Your overall mastery is impressive.';
    return `${skillsText} Continue to sharpen your blade across all disciplines.`;
  }

  if (overallScore >= 60) {
    const strengthText = strongSkills.length > 0
      ? `Your current strength lies in ${strongSkills.join(', ')}. `
      : '';
    const weaknessText = weakSkills.length > 0
      ? `Focus your training on ${weakSkills.join(', ')} to achieve balance.`
      : 'We shall build upon this foundation.';
    return `${strengthText}${weaknessText}`;
  }

  // Score < 60
  return `Every master begins as a student. Your journey starts here. I recommend focusing on fundamental techniques to build a strong foundation.`;
}

/**
 * Get message type based on context
 */
export function getMessageType(context: MessageContext): MessageType {
  switch (context) {
    case 'lesson_complete':
    case 'mastery':
      return 'celebration';
    case 'wrong_answer':
      return 'encouragement';
    case 'hint':
    case 'assessment_complete':
      return 'wisdom';
    case 'assessment_start':
    case 'practice_greeting':
    case 'timeout':
      return 'guidance';
    default:
      return 'guidance';
  }
}
